import { Component, inject } from '@angular/core';
import { EChartsOption } from 'echarts';

import { ExportService, IExportBlock, IExportColumn } from '../../services/export.service';
import { FilterService, IFilterContext } from '../../services/filter.service';

import { ITab } from '../../components/section-tabs/section-tabs.component';
import { StatColor } from '../../components/stat-card/stat-card.component';
import { COLUMNAS_PLANES, IActionPlanRow, PLANES_ACCION } from '../../data/action-plans.data';
import {
  ALERTAS_ATENDIDAS,
  ALERTAS_PENDIENTES,
  CLOSE_LOOP_KPIS,
  COLUMNAS_ATENDIDAS,
  COLUMNAS_PENDIENTES,
  HISTORICO_DETRACTORES,
  IAlertaAtendida,
  IAlertaPendiente,
  ICloseLoopKpi,
} from '../../data/close-loop.data';
import { FILTROS_BASE, FILTROS_DEFAULT, FILTROS_PLANES } from '../../data/filters.data';
import { FilterField } from '../../models/filter-option.model';
import { ISeriesPoint } from '../../models/series-point.model';
import { TableColumn } from '../../models/table-column.model';
import { ChartService } from '../../services/chart.service';

/** Contenido del modal que abre el icono de la columna DETALLE. */
export interface IDetalleAlerta {
  sucursal: string;
  region: string;
  zona: string;
  estado: string;
  recomendacion: number;
  categoria: string;
  comentario: string;
}

/**
 * Piel de cada KPI de close the loop. El color no es decorativo: conserva el
 * que el programa ya daba a cada estatus, y el icono lo acompaña para que el
 * color nunca sea el unico indicador.
 */
const KPI_SKIN: Record<string, { color: StatColor; icon: string }> = {
  'Cerradas':     { color: 'purple',  icon: 'bi-check2-circle' },
  'En proceso':   { color: 'green',   icon: 'bi-arrow-repeat' },
  'No atendidas': { color: 'magenta', icon: 'bi-exclamation-triangle' },
  'Recibidas':    { color: 'teal',    icon: 'bi-inbox' },
};

/** Campos de la seleccion que existen como columna en una fila de alerta. */
const CAMPOS_ALERTA = ['region', 'zona', 'sucursal', 'estado'] as const;

/** Los planes no traen estado, pero si el usuario que capturo el plan. */
const CAMPOS_PLAN = ['region', 'zona', 'sucursal', 'usuario'] as const;

/**
 * Alertas que siguen sin atenderse. Es la cifra del programa (KPI «No
 * atendidas»), no el numero de filas de la muestra: eso es lo que anuncia el
 * contador de la pestaña.
 */
const ALERTAS_SIN_ATENDER: number =
  CLOSE_LOOP_KPIS.find((kpi) => kpi.label === 'No atendidas')?.value ?? 0;

/**
 * Seccion «Acciones» — ¿que hacemos?
 *
 * Reune las dos pantallas que cierran el circulo con el cliente: las alertas de
 * close the loop (pestaña «Alertas») y la revision de planes de accion
 * (pestaña «Planes»).
 */
@Component({
  selector: 'app-actions-page',
  standalone: false,
  templateUrl: './actions-page.component.html',
  styleUrl: './actions-page.component.scss',
})
export class ActionsPageComponent {

  public tabs: Array<ITab> = [
    { id: 'alertas', label: 'Alertas', icon: 'bi-bell', count: ALERTAS_SIN_ATENDER },
    { id: 'planes',  label: 'Planes',  icon: 'bi-clipboard-check' },
  ];

  public tab = 'alertas';

  public seleccion: Record<string, Array<string>> = { ...FILTROS_DEFAULT };

  private readonly filterService: FilterService = inject(FilterService);
  private readonly exportService: ExportService = inject(ExportService);

  private ctx: IFilterContext = this.filterService.contexto(this.seleccion);

  /** Puntos del histórico de detractores, aparte de su `EChartsOption`. */
  private serieDetractores: Array<ISeriesPoint> = [];

  /** Qué está acotando la vista; vacío cuando no hay filtros de universo. */
  public alcance: string = '';

  /* ------------------------------ pestaña Alertas ------------------------- */

  /**
   * Vive como campo y no como literal en la plantilla: un arreglo nuevo en cada
   * ciclo de detección vuelve a disparar el `ngOnChanges` de <app-data-table>,
   * que reinicia la paginación y cierra las filas desplegadas.
   */
  public readonly exportacion: Array<string> = ['Copiar', 'CSV', 'PDF', 'Print'];

  public kpis: Array<ICloseLoopKpi> = CLOSE_LOOP_KPIS;
  public readonly columnasPendientes: Array<TableColumn> = COLUMNAS_PENDIENTES;
  public readonly columnasAtendidas: Array<TableColumn> = COLUMNAS_ATENDIDAS;
  public historico!: EChartsOption;

  public alertasPendientes: Array<IAlertaPendiente> = ALERTAS_PENDIENTES;
  public alertasAtendidas: Array<IAlertaAtendida> = ALERTAS_ATENDIDAS;

  /* ------------------------------- pestaña Planes ------------------------- */

  public readonly columnasPlanes: Array<TableColumn> = COLUMNAS_PLANES;
  public planes: Array<IActionPlanRow> = PLANES_ACCION;

  public detalle?: IDetalleAlerta;

  constructor(private chartService: ChartService) {
    this.aplicar();
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR PESTAÑAS                               */
  /* -------------------------------------------------------------------------- */

  /** Los planes filtran ademas por estatus, usuario y categoria. */
  public get filtros(): Array<FilterField> {
    return this.tab === 'planes' ? FILTROS_PLANES : FILTROS_BASE;
  }

  /** Cambiar de pestaña cierra el detalle abierto: pertenece a las alertas. */
  public onTab(tab: string): void {
    this.tab = tab;
    this.detalle = undefined;
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR KPIs                                 */
  /* -------------------------------------------------------------------------- */

  public colorKpi(kpi: ICloseLoopKpi): StatColor {
    return KPI_SKIN[kpi.label]?.color ?? 'teal';
  }

  public iconoKpi(kpi: ICloseLoopKpi): string {
    return KPI_SKIN[kpi.label]?.icon ?? 'bi-bell';
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR FILTROS                               */
  /* -------------------------------------------------------------------------- */

  public onFiltros(valores: Record<string, Array<string>>): void {
    this.seleccion = valores;
    this.ctx = this.filterService.contexto(valores);
    this.detalle = undefined;
    this.aplicar();
  }

  /**
   * Las tres tablas de esta sección traen la jerarquía encima, así que aquí
   * casi todo es filtrado real: se quitan renglones, no se maquillan cifras.
   * Lo único derivado son las cuatro tarjetas de conteo y el histórico de
   * detractores, que el programa publica como totales sin desglose.
   */
  private aplicar(): void {
    const ctx = this.ctx;

    this.alcance = this.filterService.resumenAlcance(ctx);

    this.alertasPendientes = this.filterService.filas(ALERTAS_PENDIENTES, this.seleccion, CAMPOS_ALERTA);
    this.alertasAtendidas = this.filterService.filas(ALERTAS_ATENDIDAS, this.seleccion, CAMPOS_ALERTA);
    this.planes = this.filterService.filas(PLANES_ACCION, this.seleccion, CAMPOS_PLAN);

    const factor = this.filterService.factorMuestra(ctx);

    this.kpis = CLOSE_LOOP_KPIS.map((kpi) => ({
      ...kpi,
      value: Math.max(0, Math.round(kpi.value * factor)),
    }));

    this.serieDetractores = this.filterService.serieConteo(HISTORICO_DETRACTORES, ctx);
    this.historico = this.chartService.countLine(
      this.serieDetractores,
      ChartService.COLORS.magenta,
    );

    // La insignia de la pestaña anuncia la cifra del programa, no el número de
    // filas de la muestra: sigue al KPI «No atendidas» aunque éste se acote.
    const sinAtender = this.kpis.find((kpi) => kpi.label === 'No atendidas')?.value ?? 0;

    this.tabs = [
      { id: 'alertas', label: 'Alertas', icon: 'bi-bell', count: sinAtender },
      { id: 'planes',  label: 'Planes',  icon: 'bi-clipboard-check' },
    ];
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR EXPORTACION                             */
  /* -------------------------------------------------------------------------- */

  /** Contexto que cada `<app-data-table>` escribe en la cabecera del archivo. */
  public get filtrosTexto(): string {
    return this.filterService.descripcion(this.seleccion, this.filtros);
  }

  /** Baja la pestaña abierta: las alertas con sus KPIs, o los planes. */
  public descargar(): void {
    const planes = this.tab === 'planes';

    this.exportService.csv(
      this.bloques(),
      this.exportService.nombreArchivo('nps', 'acciones', planes ? 'planes' : 'alertas'),
      {
        titulo: `Acciones · ${planes ? 'Planes' : 'Alertas'}`,
        filtros: this.filtrosTexto,
      },
    );
  }

  private bloques(): Array<IExportBlock> {
    if (this.tab === 'planes') {
      return [{
        titulo: 'Revisión de planes de acción',
        columnas: this.columnasExportables(COLUMNAS_PLANES),
        filas: this.planes,
      }];
    }

    return [
      {
        titulo: 'Close the loop · resumen',
        columnas: [
          { key: 'label', title: 'Estatus' },
          { key: 'value', title: 'Alertas' },
        ],
        filas: this.kpis,
      },
      {
        titulo: 'Histórico de detractores',
        columnas: [
          { key: 'label', title: 'Mes' },
          { key: 'value', title: 'Detractores' },
        ],
        filas: this.serieDetractores,
      },
      {
        titulo: 'Alertas pendientes por atender',
        columnas: this.columnasExportables(COLUMNAS_PENDIENTES),
        filas: this.alertasPendientes,
      },
      {
        titulo: 'Alertas atendidas',
        columnas: this.columnasExportables(COLUMNAS_ATENDIDAS),
        filas: this.alertasAtendidas,
      },
    ];
  }

  /** Las columnas de icono son controles: en un archivo sólo dejan un hueco. */
  private columnasExportables(columnas: Array<TableColumn>): Array<IExportColumn> {
    return columnas
      .filter((columna) => columna.cellStyle !== 'icon')
      .map((columna) => ({ key: columna.key, title: columna.title, suffix: columna.suffix }));
  }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR DETALLE ALERTA                           */
  /* -------------------------------------------------------------------------- */

  /** El icono de la columna DETALLE muestra el comentario completo de la fila. */
  public onDetalle(evento: { row: any; column: TableColumn }): void {
    const fila = evento.row;

    this.detalle = {
      sucursal: fila.sucursal,
      region: fila.region,
      zona: fila.zona,
      estado: fila.estado,
      recomendacion: fila.recomendacion,
      categoria: fila.categoria || fila.tipoAlerta || 'SIN CATEGORÍA',
      comentario: fila.comentario,
    };
  }

  public cerrarDetalle(): void {
    this.detalle = undefined;
  }
}
